/**
 * Your own account: display name and password.
 *
 * Open to every role, because it is about you rather than about the plant.
 * There is no user picker and no id field anywhere on it — the server reads
 * the signed-in user from the token and would refuse one anyway. Changing
 * somebody else's account is an administrator's job on an administrator's
 * screen.
 *
 * The email is shown and not editable. It is the identity every audit
 * reference resolves through, so moving it would change who wrote the ledger.
 */

import { useState } from "react";
import { ApiError } from "../../lib/api";
import { auth } from "../../lib/auth";
import type { User } from "../../lib/types";

const MIN_PASSWORD = 10;

interface Props {
  user: User;
  onUserChange: (user: User) => void;
}

type Note = { tone: "positive" | "negative"; text: string } | null;

export function MyAccount({ user, onUserChange }: Props) {
  const [name, setName] = useState(user.name);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameNote, setNameNote] = useState<Note>(null);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwNote, setPwNote] = useState<Note>(null);

  const nameChanged = name.trim() !== user.name && name.trim().length >= 2;

  // Checked here so the person is told before they submit, and again on the
  // server, which is where it is actually enforced.
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmitPassword =
    current.length > 0 &&
    next.length >= MIN_PASSWORD &&
    next === confirm &&
    !pwBusy;

  async function saveName(event: React.FormEvent) {
    event.preventDefault();
    setNameBusy(true);
    setNameNote(null);
    try {
      const updated = await auth.updateName(name.trim());
      onUserChange(updated);
      setName(updated.name);
      setNameNote({ tone: "positive", text: "Your name is updated." });
    } catch (err) {
      setNameNote({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setNameBusy(false);
    }
  }

  async function savePassword(event: React.FormEvent) {
    event.preventDefault();
    setPwBusy(true);
    setPwNote(null);
    try {
      const result = await auth.changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setPwNote({ tone: "positive", text: result.note });
    } catch (err) {
      setPwNote({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">My account</h2>
          <p className="page__sub">
            Your name and your password — nobody else's
          </p>
        </div>
      </header>

      <section className="card account__card">
        <h3 className="section-title">Who you are</h3>
        <p className="section-note">
          Your email is how the ledger identifies you: every entry, submission
          and approval you have ever made resolves through it, so it does not
          change here. Ask a super admin if it genuinely needs to.
        </p>

        <form className="account__form" onSubmit={saveName}>
          <label className="inputgroup">
            <span className="label">Email</span>
            <input value={user.email} readOnly disabled />
          </label>
          <label className="inputgroup inputgroup--grow">
            <span className="label">Display name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoComplete="name"
            />
          </label>
          <label className="inputgroup">
            <span className="label">Role</span>
            <input
              value={user.role.replace(/_/g, " ").toLowerCase()}
              readOnly
              disabled
            />
          </label>
          <button className="btn btn--auto" disabled={!nameChanged || nameBusy}>
            {nameBusy ? "Saving…" : "Save name"}
          </button>
        </form>

        {nameNote && (
          <div className={`banner banner--${nameNote.tone}`} role="status">
            {nameNote.text}
          </div>
        )}
      </section>

      <section className="card account__card">
        <h3 className="section-title">Password</h3>
        <p className="section-note">
          Changing it signs out every other device on this account — that is
          what makes it useful after a lost phone, not just good hygiene. You
          stay signed in here. At least {MIN_PASSWORD} characters: a long
          phrase you can remember beats a short one you cannot.
        </p>

        <form className="account__form" onSubmit={savePassword}>
          <label className="inputgroup">
            <span className="label">Current password</span>
            <input
              type={reveal ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <label className="inputgroup">
            <span className="label">New password</span>
            <input
              type={reveal ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              aria-describedby={tooShort ? "pw-short" : undefined}
            />
          </label>
          <label className="inputgroup">
            <span className="label">Repeat new password</span>
            <input
              type={reveal ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              aria-describedby={mismatch ? "pw-mismatch" : undefined}
            />
          </label>
          <button className="btn btn--auto" disabled={!canSubmitPassword}>
            {pwBusy ? "Changing…" : "Change password"}
          </button>
        </form>

        <label className="checkbox account__reveal">
          <input
            type="checkbox"
            checked={reveal}
            onChange={(e) => setReveal(e.target.checked)}
          />
          <span>Show what I am typing</span>
        </label>

        {/* Told before submitting, not after a round trip. Both carry a glyph
            as well as a colour — meaning never rides on hue alone (7.5). */}
        {tooShort && (
          <p className="account__hint" id="pw-short">
            ✕ {MIN_PASSWORD - next.length} more character
            {MIN_PASSWORD - next.length === 1 ? "" : "s"} needed.
          </p>
        )}
        {mismatch && (
          <p className="account__hint" id="pw-mismatch">
            ✕ The two new passwords do not match.
          </p>
        )}

        {pwNote && (
          <div className={`banner banner--${pwNote.tone}`} role="alert">
            {pwNote.text}
          </div>
        )}
      </section>
    </div>
  );
}
